import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Typography,
  Button,
  Spin,
  Alert,
  Divider,
  App,
  Badge,
} from "antd";
import {
  CircleCheck,
  CircleX,
  ArrowLeft,
  ArrowRight,
  Trophy,
  Home,
  RotateCcw,
  Check,
} from "lucide-react";
import Icon from "../../components/Icon";

import { useIsMobile } from "../../hooks/useBreakpoint";
import MainLayout from "../../components/MainLayout";
import Header from "../../components/Header";
import { useAuth } from "../../auth/auth.context";
import { useUserProgress } from "../../hooks/useUserProgress";
import { useThemeMode } from "../../theme/ThemeProvider";
import {
  getModule,
  getNextModuleInCourse,
  isLastModuleInCourse,
  findCourseByModuleId,
} from "../../services/moduleService";
import {
  getQuizPassingScore,
  getQuizAnswers,
  getQuizQuestionUseQuery,
} from "../../services/quizService";
import {
  updateModuleProgress,
  completeCourse,
} from "../../services/userProgressService";
import { useModuleQuiz } from "../../hooks/useModuleQuiz";
import Fireworks from "../ModuleViewer/Fireworks";

const { Title, Text, Paragraph } = Typography;

/* Wrapper: full-screen trên mobile, MainLayout trên desktop */
function QuizLayout({ isMobile, children, navBar }) {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  if (isMobile) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Header />
        {/* Sticky nav bar ngay dưới header */}
        {navBar && (
          <div
            style={{
              position: "sticky",
              top: 72,
              zIndex: 100,
              backgroundColor: isDark ? "#161b22" : "#ffffff",
              borderBottom: `1px solid ${isDark ? "#30363d" : "#e2e7ff"}`,
              padding: "10px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            {navBar}
          </div>
        )}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            paddingTop: navBar ? 0 : 72,
            color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.88)",
          }}
        >
          {children}
        </div>
      </div>
    );
  }
  return <MainLayout>{children}</MainLayout>;
}

function ModuleQuiz() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isModuleCompleted, refreshProgress } = useUserProgress();
  const isMobile = useIsMobile();
  const { message } = App.useApp();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [module, setModule] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [passingScore, setPassingScore] = useState(70);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [completedCourseName, setCompletedCourseName] = useState(null);
  const [showFireworks, setShowFireworks] = useState(false);

  const { data: quizDataApi, isLoading: isQuizLoading, error: quizError } = useModuleQuiz(moduleId);
  const dataLoadedRef = useRef(false);

  const quizData = useMemo(() => {
    if (!quizDataApi || isQuizLoading) return [];
    return getQuizQuestionUseQuery(quizDataApi);
  }, [quizDataApi, isQuizLoading]);

  useEffect(() => {
    dataLoadedRef.current = false;
  }, [moduleId]);

  useEffect(() => {
    if (dataLoadedRef.current || isQuizLoading) return;

    if (!currentUser) {
      setError("You must be logged in to take a quiz");
      setLoading(false);
      return;
    }
    if (quizError) {
      setError(`Failed to load quiz data: ${quizError.message || "Unknown error"}`);
      setLoading(false);
      return;
    }
    if (!quizData || quizData.length === 0) {
      setError("No quiz questions found for this module");
      setLoading(false);
      return;
    }

    const loadQuizData = async () => {
      try {
        setLoading(true);
        setError(null);

        const moduleData = await getModule(moduleId);
        if (!moduleData) {
          setError("Module not found");
          setLoading(false);
          return;
        }
        setModule(moduleData);

        if (isModuleCompleted(moduleId)) {
          message.info("You have already completed this module");
        }

        const isValidQuizData = quizData.every(
          (q) =>
            q.id &&
            q.text &&
            Array.isArray(q.options) &&
            q.options.length > 0 &&
            q.options.every((o) => o.id && o.text !== undefined)
        );
        if (!isValidQuizData) {
          setError("Quiz data is in an invalid format. Please contact support.");
          setLoading(false);
          return;
        }

        setQuestions(quizData);
        const modulePassingScore = await getQuizPassingScore(moduleId);
        setPassingScore(modulePassingScore);

        const initialAnswers = {};
        quizData.forEach((q) => {
          initialAnswers[q.id] = q.type === "multiple" ? [] : "";
        });
        setAnswers(initialAnswers);
        setLoading(false);
        dataLoadedRef.current = true;
      } catch (err) {
        setError(`Failed to load quiz data: ${err.message || "Unknown error"}`);
        setLoading(false);
      }
    };

    loadQuizData();
  }, [moduleId, currentUser, isQuizLoading, quizError, quizData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswerChange = (questionId, value) => {
    const question = questions.find((q) => q.id === questionId);
    if (question.type === "multiple") {
      setAnswers((prev) => {
        const current = [...prev[questionId]];
        return {
          ...prev,
          [questionId]: current.includes(value)
            ? current.filter((a) => a !== value)
            : [...current, value],
        };
      });
    } else {
      setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }
  };

  const handleSubmit = async () => {
    let correctAnswers = 0;
    let answersFromServer = [];
    try {
      answersFromServer = await getQuizAnswers(moduleId);
      setQuizAnswers(answersFromServer);
    } catch {
      setQuizAnswers([]);
    }

    questions.forEach((question) => {
      const userAnswer = answers[question.id];
      const serverAnswer = answersFromServer.find((a) => a.id === question.id);
      const correctIndexes = Array.isArray(serverAnswer?.correctAnswer)
        ? serverAnswer.correctAnswer
        : [];

      if (question.type === "multiple") {
        const correctOptions = correctIndexes.map((idx) => question.options[idx]?.id);
        const isCorrect =
          userAnswer.length === correctOptions.length &&
          correctOptions.every((id) => userAnswer.includes(id));
        if (isCorrect) correctAnswers++;
      } else {
        const correctOptionId = question.options[correctIndexes[0]]?.id;
        if (correctOptionId && userAnswer === correctOptionId) correctAnswers++;
      }
    });

    const finalScore = Math.round((correctAnswers / questions.length) * 100);
    setScore(finalScore);
    setSubmitted(true);

    try {
      await updateModuleProgress(
        currentUser.uid, moduleId, "", false, finalScore, module.version
      );
      refreshProgress();

      const passed = finalScore >= passingScore;
      if (passed) {
        try {
          const { isLast, course } = await isLastModuleInCourse(
            moduleId,
            (id) => id === moduleId || isModuleCompleted(id),
            currentUser?.uid
          );
          if (isLast && course) {
            setCompletedCourseName(course.name);
            try { await completeCourse(currentUser.uid, course.id); } catch {}
            setTimeout(() => setShowFireworks(true), 1000);
          }
        } catch {}
      }

      message.info(
        passed
          ? `Quiz submitted! Your score: ${finalScore}%. Module completed!`
          : `Quiz submitted! Your score: ${finalScore}%. You need ${passingScore}% to pass. Please try again.`
      );
    } catch {
      message.error("Error saving your progress");
    }
  };

  const handleNext = () =>
    setCurrentQuestionIndex((p) => Math.min(p + 1, questions.length - 1));
  const handleBack = () =>
    setCurrentQuestionIndex((p) => Math.max(p - 1, 0));

  const handleContinueToNextModule = async () => {
    try {
      const nextModule = await getNextModuleInCourse(moduleId, currentUser?.uid);
      navigate(nextModule ? `/module/${nextModule.id}` : "/");
    } catch {
      navigate("/");
    }
  };

  const handleRetakeCourse = async () => {
    try {
      const course = await findCourseByModuleId(moduleId, currentUser?.uid);
      if (course?.modules?.length > 0) {
        navigate(`/module/${course.modules[0].id}?retake=true`);
      } else {
        navigate("/");
      }
    } catch {
      navigate("/");
    }
  };

  const handleFireworksComplete = () => setShowFireworks(false);

  /* ── Theme-aware tokens ── */
  const tokens = {
    bg: isDark ? "#0d1117" : "#faf8ff",
    cardBg: isDark ? "#161b22" : "#ffffff",
    cardBorder: isDark ? "#30363d" : "#e2e7ff",
    cardShadow: isDark
      ? "0 4px 24px rgba(0,0,0,0.4)"
      : "0 4px 24px rgba(0,62,199,0.08)",
    labelColor: isDark ? "#79c0ff" : "var(--color-primary)",
    textPrimary: isDark ? "#e6edf3" : "var(--color-on-surface)",
    textSecondary: isDark ? "#8b949e" : "var(--color-on-surface-variant)",
    progressTrack: isDark ? "#21262d" : "var(--color-surface-container-high)",
    progressFill: isDark ? "#388bfd" : "var(--color-secondary)",
    submitBg: isDark ? "#388bfd" : "var(--color-secondary)",
    submitText: isDark ? "#ffffff" : "var(--color-on-secondary)",
    optionBg: isDark ? "#0d1117" : "var(--color-surface)",
    optionBgSelected: isDark ? "#1c2a3f" : "var(--color-primary-fixed)",
    optionBorder: isDark ? "#30363d" : "var(--color-outline-variant)",
    optionBorderSelected: isDark ? "#388bfd" : "var(--color-primary)",
    optionShadowSelected: isDark
      ? "0 2px 8px rgba(56,139,253,0.2)"
      : "0 2px 8px rgba(0,62,199,0.12)",
    indicatorBg: isDark ? "#388bfd" : "var(--color-primary)",
    divider: isDark ? "#21262d" : undefined,
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <MainLayout>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <MainLayout>
        <div style={{ padding: 24 }}>
          <Alert type="error" message={error} showIcon />
          <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Button icon={<Icon as={ArrowLeft} />} onClick={() => navigate(`/module/${moduleId}`)}>
              Back to Module
            </Button>
            <Button icon={<Icon as={Home} />} onClick={() => navigate("/")}>
              Back to Home
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  /* ── Results ── */
  if (submitted) {
    const passed = score >= passingScore;

    return (
      <QuizLayout isMobile={isMobile}>
        <div
          style={{
            flex: isMobile ? 1 : undefined,
            minHeight: isMobile ? undefined : "100vh",
            backgroundColor: tokens.bg,
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "stretch" : "center",
            padding: isMobile ? "0" : "56px 24px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: isMobile ? "100%" : 720,
              backgroundColor: tokens.cardBg,
              border: isMobile ? "none" : `1px solid ${tokens.cardBorder}`,
              borderRadius: isMobile ? 0 : 12,
              boxShadow: isMobile ? "none" : tokens.cardShadow,
              padding: isMobile ? "24px 20px 100px" : "44px 52px",
              flex: isMobile ? 1 : undefined,
            }}
          >
            {/* Score circle */}
            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 104,
                  height: 104,
                  borderRadius: "50%",
                  backgroundColor: passed
                    ? isDark ? "rgba(0,62,199,0.12)" : "#f0f5ff"
                    : isDark ? "rgba(248,81,73,0.12)" : "#fff5f5",
                  border: `2px solid ${passed ? "var(--color-primary)" : (isDark ? "#f85149" : "#ff4d4f")}`,
                  marginBottom: 20,
                }}
              >
                <Icon
                  as={Trophy}
                  size={36}
                  strokeWidth={2.5}
                  style={{
                    color: passed
                      ? "var(--color-primary)"
                      : isDark ? "#f85149" : "#faad14",
                    marginBottom: 2,
                  }}
                />
                <Text
                  strong
                  style={{
                    fontSize: 18,
                    color: passed
                      ? "var(--color-primary)"
                      : isDark ? "#f85149" : "#ff4d4f",
                    lineHeight: 1,
                  }}
                >
                  {score}%
                </Text>
              </div>

              <Title level={3} style={{ marginBottom: 8, color: tokens.textPrimary }}>
                Quiz Results
              </Title>
              <Paragraph style={{ color: tokens.textSecondary, marginBottom: 0, fontSize: 15 }}>
                {passed
                  ? completedCourseName
                    ? `Congratulations! You've completed the "${completedCourseName}" course.`
                    : "Great work! You've passed this module."
                  : `You need at least ${passingScore}% to pass. Review and try again.`}
              </Paragraph>
            </div>

            <Divider style={isDark ? { borderColor: tokens.divider } : {}}>
              <Text style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: tokens.textSecondary }}>
                Question Summary
              </Text>
            </Divider>

            <div style={{ marginTop: 16 }}>
              {questions.map((question, index) => {
                const userAnswer = answers[question.id];
                const serverAnswer = quizAnswers.find((a) => a.id === question.id);
                const correctIndexes = Array.isArray(serverAnswer?.correctAnswer)
                  ? serverAnswer.correctAnswer
                  : [];
                const explanation = serverAnswer?.explanation || "";

                let isCorrect = false;
                if (question.type === "multiple") {
                  const correctOptions = correctIndexes.map((idx) => question.options[idx]?.id);
                  isCorrect =
                    userAnswer.length === correctOptions.length &&
                    correctOptions.every((id) => userAnswer.includes(id));
                } else {
                  const correctOptionId = question.options[correctIndexes[0]]?.id;
                  isCorrect = correctOptionId && userAnswer === correctOptionId;
                }

                return (
                  <div
                    key={question.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "14px 16px",
                      marginBottom: 10,
                      borderRadius: 8,
                      backgroundColor: isCorrect
                        ? isDark ? "rgba(0,62,199,0.08)" : "#f0f5ff"
                        : isDark ? "rgba(248,81,73,0.08)" : "#fff2f0",
                      border: `2px solid ${isCorrect
                        ? isDark ? "rgba(0,62,199,0.3)" : "#adc6ff"
                        : isDark ? "rgba(248,81,73,0.3)" : "#ffccc7"}`,
                    }}
                  >
                    {isCorrect ? (
                      <Icon as={CircleCheck} strokeWidth={2.5} size={18} style={{ color: "var(--color-primary)", marginTop: 2, flexShrink: 0 }} />
                    ) : (
                      <Icon as={CircleX} strokeWidth={2.5} size={18} style={{ color: isDark ? "#f85149" : "#ff4d4f", marginTop: 2, flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1 }}>
                      <Text strong style={{ display: "block", color: tokens.textPrimary, lineHeight: 1.5 }}>
                        {index + 1}. {question.text}
                      </Text>
                      <Text
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: isCorrect
                            ? "var(--color-primary)"
                            : isDark ? "#f85149" : "#ff4d4f",
                        }}
                      >
                        {isCorrect ? "Correct" : "Incorrect"}
                      </Text>
                      {explanation && (
                        <Paragraph
                          style={{
                            marginTop: 8,
                            marginBottom: 0,
                            fontSize: 13,
                            color: tokens.textSecondary,
                            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                            paddingTop: 8,
                          }}
                        >
                          {explanation}
                        </Paragraph>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action buttons */}
            <div
              style={{
                marginTop: 32,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "center",
                gap: 12,
                paddingTop: 24,
                borderTop: `1px solid ${isDark ? tokens.divider : "#f0f0f0"}`,
              }}
            >
              {passed ? (
                completedCourseName ? (
                  <>
                    <Button
                      type="primary"
                      icon={<Icon as={Home} />}
                      onClick={() => navigate("/")}
                      block={isMobile}
                    >
                      Back to Home
                    </Button>
                    <Button
                      icon={<Icon as={RotateCcw} />}
                      onClick={handleRetakeCourse}
                      block={isMobile}
                    >
                      Retake Course
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      icon={<Icon as={ArrowLeft} />}
                      onClick={() => navigate(`/module/${moduleId}`)}
                      block={isMobile}
                    >
                      Back to Module
                    </Button>
                    <Button
                      type="primary"
                      icon={<Icon as={ArrowRight} />}
                      onClick={handleContinueToNextModule}
                      block={isMobile}
                    >
                      Next Module
                    </Button>
                  </>
                )
              ) : (
                <>
                  <Button
                    icon={<Icon as={ArrowLeft} />}
                    onClick={() => navigate(`/module/${moduleId}`)}
                    block={isMobile}
                  >
                    Back to Module
                  </Button>
                  <Button
                    type="primary"
                    icon={<Icon as={RotateCcw} />}
                    onClick={() => window.location.reload()}
                    block={isMobile}
                  >
                    Try Again
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <Fireworks
          show={showFireworks}
          onComplete={handleFireworksComplete}
          courseName={completedCourseName}
        />
      </QuizLayout>
    );
  }

  /* ── Quiz view ── */
  const currentQuestion = questions[currentQuestionIndex];
  const totalQ = questions.length;
  const progressPct = Math.round(((currentQuestionIndex + 1) / totalQ) * 100);
  const isLastQuestion = currentQuestionIndex === totalQ - 1;
  const hasAnsweredCurrent = currentQuestion
    ? Array.isArray(answers[currentQuestion.id])
      ? answers[currentQuestion.id].length > 0
      : !!answers[currentQuestion.id]
    : false;
  const allAnswered = Object.values(answers).every(
    (a) => (Array.isArray(a) ? a.length > 0 : !!a)
  );

  return (
    <QuizLayout
      isMobile={isMobile}
      navBar={isMobile ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Button
              icon={<Icon as={ArrowLeft} />}
              onClick={() => navigate(`/module/${moduleId}`)}
              type="text"
              style={{ color: tokens.textSecondary, padding: 0 }}
            />
            <div style={{ width: 1, height: 16, backgroundColor: tokens.cardBorder, margin: "0 4px" }} />
            <Button
              disabled={currentQuestionIndex === 0}
              onClick={handleBack}
              type="text"
              style={{ color: tokens.textSecondary, padding: "0 4px" }}
            >
              Previous
            </Button>
          </div>
          {isLastQuestion ? (
            <Button
              type="primary"
              style={{
                backgroundColor: allAnswered ? tokens.submitBg : undefined,
                borderColor: allAnswered ? tokens.submitBg : undefined,
                color: allAnswered ? tokens.submitText : undefined,
              }}
              disabled={!allAnswered}
              onClick={handleSubmit}
            >
              Submit Quiz
            </Button>
          ) : (
            <Button
              type="primary"
              disabled={!hasAnsweredCurrent}
              onClick={handleNext}
            >
              Next <Icon as={ArrowRight} />
            </Button>
          )}
        </>
      ) : null}
    >
      <div
        style={{
          flex: isMobile ? 1 : undefined,
          minHeight: isMobile ? undefined : "100vh",
          backgroundColor: tokens.bg,
          display: "flex",
          flexDirection: "column",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: isMobile ? "flex-start" : "center",
          padding: isMobile ? "0" : "56px 24px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: isMobile ? "100%" : 680,
            backgroundColor: tokens.cardBg,
            border: isMobile ? "none" : `1px solid ${tokens.cardBorder}`,
            borderRadius: isMobile ? 0 : 12,
            boxShadow: isMobile ? "none" : tokens.cardShadow,
            padding: isMobile ? "20px 20px 100px" : "40px 48px",
            paddingTop: isMobile ? 100 : 40,
            flex: isMobile ? 1 : undefined,
          }}
        >
          {/* ── Back to Module (Desktop Only) ── */}
          {!isMobile && (
            <Button
              type="text"
              icon={<Icon as={ArrowLeft} />}
              onClick={() => navigate(`/module/${moduleId}`)}
              style={{ 
                marginBottom: 16, 
                padding: 0, 
                color: tokens.textSecondary,
                display: "flex",
                alignItems: "center",
                fontSize: 13
              }}
            >
              Back to Module
            </Button>
          )}

          {/* ── Header ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ flex: 1 }}>
              <Text
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: tokens.labelColor,
                  marginBottom: 4,
                }}
              >
                Module Assessment
              </Text>
              <Title
                level={3}
                style={{
                  marginBottom: 0,
                  color: tokens.textPrimary,
                  fontSize: 20,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {module?.name}
              </Title>
            </div>
            <Badge
              count={`${currentQuestionIndex + 1} / ${totalQ}`}
              style={{
                backgroundColor: isDark ? "#21262d" : "var(--color-surface-container)",
                color: tokens.textSecondary,
                fontWeight: 500,
                fontSize: 13,
                boxShadow: "none",
                borderRadius: 20,
                padding: "0 12px",
                height: 28,
                lineHeight: "28px",
                marginTop: 4,
              }}
            />
          </div>

          {/* ── Progress bar ── */}
          <div
            style={{
              width: "100%",
              height: 4,
              backgroundColor: tokens.progressTrack,
              borderRadius: 99,
              overflow: "hidden",
              marginBottom: 32,
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${progressPct}%`,
                backgroundColor: tokens.progressFill,
                borderRadius: 99,
                transition: "width 0.4s ease-out",
              }}
            />
          </div>

          {/* ── Question ── */}
          {currentQuestion && (
            <>
              <Paragraph
                style={{
                  fontSize: 17,
                  fontWeight: 400,
                  color: tokens.textPrimary,
                  lineHeight: 1.65,
                  marginBottom: 20,
                }}
              >
                {currentQuestion.text}
              </Paragraph>

              <Text
                style={{
                  display: "block",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: tokens.textSecondary,
                  marginBottom: 14,
                }}
              >
                {currentQuestion.type === "multiple"
                  ? "Select all that apply"
                  : "Select one answer"}
              </Text>

              {/* ── Options ── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {currentQuestion.options.map((option) => {
                  const isSelected =
                    currentQuestion.type === "multiple"
                      ? (answers[currentQuestion.id] || []).includes(option.id)
                      : answers[currentQuestion.id] === option.id;

                  return (
                    <div
                      key={option.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleAnswerChange(currentQuestion.id, option.id)}
                      onKeyDown={(e) =>
                        e.key === "Enter" &&
                        handleAnswerChange(currentQuestion.id, option.id)
                      }
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "14px 18px",
                        border: `1.5px solid ${isSelected ? tokens.optionBorderSelected : tokens.optionBorder}`,
                        borderRadius: 8,
                        backgroundColor: isSelected ? tokens.optionBgSelected : tokens.optionBg,
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        boxShadow: isSelected ? tokens.optionShadowSelected : "none",
                        userSelect: "none",
                      }}
                    >
                      {/* Indicator */}
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          flexShrink: 0,
                          borderRadius: currentQuestion.type === "multiple" ? 4 : "50%",
                          border: `2px solid ${isSelected ? tokens.indicatorBg : tokens.optionBorder}`,
                          backgroundColor: isSelected ? tokens.indicatorBg : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isSelected && (
                          currentQuestion.type === "multiple" ? (
                            <Icon as={Check} size={11} style={{ color: "#fff" }} />
                          ) : (
                            <div
                              style={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                backgroundColor: "#fff",
                              }}
                            />
                          )
                        )}
                      </div>

                      <Text
                        style={{
                          fontSize: 15,
                          fontWeight: isSelected ? 500 : 400,
                          color: tokens.textPrimary,
                          lineHeight: 1.5,
                        }}
                      >
                        {option.text}
                      </Text>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Navigation (desktop: inline bottom) ── */}
          {!isMobile && (
            <>
              <Divider style={isDark ? { borderColor: tokens.divider, marginTop: 32 } : { marginTop: 32 }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <Button
                  icon={<Icon as={ArrowLeft} />}
                  disabled={currentQuestionIndex === 0}
                  onClick={handleBack}
                  type="text"
                  style={{ color: tokens.textSecondary }}
                >
                  Previous
                </Button>
                {isLastQuestion ? (
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: allAnswered ? tokens.submitBg : undefined,
                      borderColor: allAnswered ? tokens.submitBg : undefined,
                      color: allAnswered ? tokens.submitText : undefined,
                    }}
                    disabled={!allAnswered}
                    onClick={handleSubmit}
                  >
                    Submit Quiz
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    disabled={!hasAnsweredCurrent}
                    onClick={handleNext}
                  >
                    Next <Icon as={ArrowRight} />
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </QuizLayout>
  );
}

export default ModuleQuiz;
